/**
 * Slash-command extension for the wiki editor — Phase 4a foundation.
 *
 * Typing `/` at the start of an empty paragraph opens
 * `<SlashCommandPopover>`. The popover scores the registry against the
 * typed query, the user picks a row via Arrow / Enter (or click), and
 * the extension's `command` override deletes the `/query` text before
 * dispatching the picked `SlashCommand.run(editor)`.
 *
 * Why extend `Mention` (not bare `Node` / `Extension.create`)?
 *
 *   Same reasoning as `wikilink-extension.ts`: `@tiptap/suggestion` +
 *   `@tiptap/core` are TRANSITIVE deps. `@tiptap/extension-mention` IS a
 *   direct dep and ships the `Suggestion(...)` wiring we need. Extending
 *   it gives us:
 *     - `addProseMirrorPlugins` → `Suggestion(...)` already wired,
 *     - `deleteTriggerWithBackspace` ergonomics,
 *     - `addOptions` slot that accepts a custom `suggestion` config.
 *
 *   Tradeoff: Mention's default schema spec registers a NEW inline atom
 *   node. We override `name: 'slashCommand'` so it's distinct from the
 *   `@user` mention + `[[wikilink]]` nodes; but the popover NEVER
 *   commits a `slashCommand` node — `command` runs the picked
 *   command's `run()` and the trigger range is deleted. The node type
 *   is never instantiated in the document. (If TipTap later forbids
 *   registering a never-used node type, the fallback is documented in
 *   the agent brief: build an `Extension.create` shell with a manual
 *   `Suggestion(...)` plugin via the same Mention transitive — same
 *   dep-boundary as today.)
 *
 * Allow predicate — when the slash menu IS allowed to open:
 *
 *   1. Caret is in a `paragraph` node (block parent).
 *   2. The caret offset within that paragraph is `0` (start-of-line),
 *      or the previous character is whitespace / an inline atom.
 *   3. No active `code` or `link` mark on the position.
 *   4. No ancestor `taskItem` / `codeBlock` between the caret and the
 *      document root.
 *
 *   Per-cell slash IS allowed: the menu opens inside `tableCell` /
 *   `tableHeader` paragraphs so a user can insert an inline atom
 *   (date / status / mention / emoji / work-item) directly into a
 *   cell. Block-level commands self-no-op when they detect a cell
 *   ancestor — see `isCursorInsideTableCell` in
 *   `src/features/wiki/lib/slash-commands.ts`. The trade-off is
 *   discoverability (cell users see the full menu) over hard-gating
 *   (which would force them to compose chips outside and paste in).
 *   This is the Confluence behavior — block-level callouts / panels
 *   silently no-op when committed inside a cell.
 *
 * Command override — what happens on commit:
 *
 *   The default Mention `command` inserts a mention node at the
 *   suggestion range. We can't do that here — the slash command IS the
 *   block insertion, not a node reference. So we:
 *     1. Delete the `/query` text via `insertContentAt(range, '')` (the
 *        plugin's stock cleanup mechanic),
 *     2. Run the picked command's `run(editor)`.
 *   The popover passes the full `SlashCommand` object as the commit
 *   payload (`props.command(picked)`); we type-coerce on receipt.
 */

import Mention, { type MentionOptions } from '@tiptap/extension-mention'
import type { ResolvedPos } from '@tiptap/pm/model'
import type { Editor as ProseMirrorEditor } from '@tiptap/react'

import type { SlashCommand } from '@/features/wiki/lib/slash-commands'

export interface BuildSlashCommandExtensionOptions {
  /**
   * The bridge object built via `buildSlashCommandSuggestion(...)`. When
   * undefined, the extension still registers (the parser is dead-easy
   * — the node type is never produced as content) but no popover opens.
   * Wiki-only surface today; other call-sites omit it.
   */
  suggestion?: MentionOptions['suggestion']
}

/**
 * Ancestor types that disable the slash menu entirely. `tableCell` /
 * `tableHeader` were here pre-2026-05-29; the locked decision opened
 * the menu inside cells so inline atoms (date / status / mention /
 * emoji / work-item) can be inserted directly. Block-level commands
 * self-no-op inside cells via the per-command guard in
 * `slash-commands.ts:isCursorInsideTableCell()`.
 */
const FORBIDDEN_ANCESTORS = new Set<string>(['taskItem', 'codeBlock'])

/**
 * Pure predicate the suggestion plugin invokes to decide whether `/` at
 * the current cursor should open the menu. Exported so the unit test
 * suite can exercise it against synthetic ProseMirror states without
 * spinning up a full Editor instance.
 */
export function isSlashMenuAllowed(
  state: {
    doc: {
      resolve: (pos: number) => ResolvedPos
      textBetween: (from: number, to: number, blockSeparator?: string, leafText?: string) => string
    }
  },
  range: { from: number },
): boolean {
  let $from: ResolvedPos
  try {
    $from = state.doc.resolve(range.from)
  } catch {
    // Defensive: a stale range (e.g. concurrent doc mutation) can
    // out-of-range — reject the suggestion rather than crash.
    return false
  }

  // 1. Immediate parent must be a paragraph.
  if ($from.parent.type.name !== 'paragraph') return false

  // 2. Boundary guard — block slash inside a word (`yes/no`), but
  //    allow slash at paragraph start, after whitespace, or after an
  //    inline atom (chip / mention / image). Earlier behavior required
  //    `parentOffset === 0` (paragraph start only), which prevented
  //    the user from inserting a second inline chip on the same line
  //    after an earlier insertion. Confluence's behavior is what we
  //    match here: slash works inline anywhere that isn't mid-word.
  if ($from.parentOffset > 0) {
    // `textBetween` with an empty leafText skips atom nodes — so an
    // atom-before-slash resolves to '' and falls through to allow.
    const charBefore = state.doc.textBetween(range.from - 1, range.from, '', '')
    if (charBefore && !/^\s/.test(charBefore)) return false
  }

  // 3. No code mark, no link mark active at this position. (Mention
  //    suggestion already rejects open mid-mention via its own machinery
  //    — those don't appear here.)
  const marks = $from.marks()
  for (const mark of marks) {
    const name = mark.type.name
    if (name === 'code' || name === 'link') return false
  }

  // 4. Walk up the ancestor chain looking for a forbidden block kind.
  //    `$from.depth` is the depth of the cursor's parent in the doc
  //    tree; depth 0 is the doc root.
  for (let d = $from.depth; d > 0; d--) {
    const ancestor = $from.node(d)
    if (FORBIDDEN_ANCESTORS.has(ancestor.type.name)) return false
  }

  return true
}

/**
 * Build the configured slash-command extension. Returns a fresh instance
 * per call — same TipTap constraint as Mention + wikilink (don't share
 * extension instances across editors).
 *
 * Wiki editor surface only — `surface === 'wiki'` in `buildEditorExtensions`.
 */
export function buildSlashCommandExtension({ suggestion }: BuildSlashCommandExtensionOptions = {}) {
  return Mention.extend({
    name: 'slashCommand',

    // The node type is registered for ProseMirror schema purposes (the
    // Mention extension's `addNodeView` + node-spec wiring all assume a
    // node exists) but we never insert one. The default Mention render
    // shape stays, since it never actually fires.
  }).configure({
    HTMLAttributes: {
      // Prefix the data-type so any unexpected serialization is
      // distinguishable from the canonical mention nodes in a DOM dump.
      'data-type': 'slash-command',
    },

    deleteTriggerWithBackspace: true,

    suggestion: (suggestion
      ? {
          ...suggestion,
          char: '/',
          // The Mention-default `allowedPrefixes: [' ']` would refuse to
          // open the menu when the trigger sits at the very start of a
          // paragraph (no preceding char). `null` opts out and our
          // own `allow` predicate handles the start-of-line check.
          allowedPrefixes: null,
          allow: ((props: {
            state: { doc: { resolve: (pos: number) => ResolvedPos } }
            range: { from: number; to: number }
            editor: unknown
            isActive?: boolean
          }) => {
            // Forward to any caller-supplied allow first (composable).
            const callerAllow = (suggestion as { allow?: (p: unknown) => boolean }).allow
            if (typeof callerAllow === 'function' && !callerAllow(props)) return false
            // The runtime `state` carries the full PM doc (which has
            // `textBetween`); the parameter type narrowing here is
            // the `isSlashMenuAllowed` predicate's structural shape,
            // so a cast through `unknown` keeps both happy.
            return isSlashMenuAllowed(
              props.state as unknown as Parameters<typeof isSlashMenuAllowed>[0],
              props.range,
            )
          }) as MentionOptions['suggestion']['allow'],
          /**
           * `command` runs the picked SlashCommand's `run(editor)` after
           * deleting the trigger range. Type-coerces the commit payload
           * (the popover passes the full SlashCommand object, not the
           * default `{ id, label }` shape) — the Mention extension's
           * suggestion typing is bound to `MentionNodeAttrs`, but the
           * suggestion plugin itself is generic over the commit payload
           * shape and `as unknown as` is the canonical cast across
           * mention / wikilink / slash families. Same pattern as
           * `wikilink-extension.ts:420`.
           */
          command: (({
            editor,
            range,
            props,
          }: {
            editor: ProseMirrorEditor
            range: { from: number; to: number }
            props: SlashCommand
          }) => {
            // Phase 1: delete the `/query` text. `insertContentAt` with
            // empty content collapses the range to the `from` position
            // and removes the typed trigger chars. Matches the Mention
            // default-cleanup shape.
            editor.chain().focus().insertContentAt(range, '').run()
            // Phase 2: run the picked command. The command itself
            // chains its own `.focus().run()` — see the registry's
            // individual entries.
            try {
              props.run(editor)
            } catch (err) {
              // `SlashCommandNotImplemented` from a future-phase entry
              // bubbles here. The popover already filtered such entries
              // out of `PHASE_4A_COMMANDS`, so this branch is defensive
              // — log + continue rather than crash the editor. The
              // user sees no insertion; the `/query` text is already
              // gone.
              console.warn('[slash-command] picked command threw', err)
            }
          }) as unknown as MentionOptions['suggestion']['command'],
        }
      : { char: '/' }) as MentionOptions['suggestion'],
  })
}
